import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UIState, ToolType, CameraViewPreset } from '../../types';
import { DEFAULT_ZOOM, GRID_COLOR, GRID_OPACITY } from '../../constants';
import { clearLayout, loadLayout } from '../layout/layoutSlice';
import { clearFloorPlan, setFloorPlan } from '../floorplan/floorplanSlice';

/** Force 2D whenever a layout / fab / project is opened, cleared, or replaced. */
function leave3dView(state: UIState) {
  state.view3dOpen = false;
  state.view3dSceneRevealed = false;
  state.view3dSimulation = false;
  state.active3DCameraOhtId = null;
  state.active3DFollowOhtId = null;
  // Opening, replacing, or clearing a project always starts in Editor.
  state.simulationPanelOpen = false;
}

const initialState: UIState = {
  activeTool: 'select',
  rightDrawerTab: 'properties',
  gridSettings: {
    enabled: true,
    size: 10,
    snapGridSize: 1,
    snapToGrid: true,
    snapToObjects: false,
    color: GRID_COLOR,
    opacity: GRID_OPACITY,
  },
  showGrid: true,
  showRulers: false,
  showAlignmentGuides: true,
  darkMode: false,
  leftSidebarOpen: true,
  rightSidebarOpen: true,
  bottomPanelOpen: false,
  dxfLayersPanelOpen: false,
  facilityPanelOpen: false,
  simulationPanelOpen: false,
  view3dOpen: false,
  view3dSceneRevealed: false,
  workspaceView: 'map',
  toolboxSettingsOpen: false,
  mindmapDrawerExtended: false,
  view3dDragLayout: false,
  view3dSimulation: false,
  active3DCameraPreset: 'default',
  cameraPresetVersion: 0,
  active3DCameraOhtId: null,
  active3DFollowOhtId: null,
  active3DFollowOhtMode: 'chase',
  zoomLevel: DEFAULT_ZOOM,
  placingStopForVehicle: null,
  measurementUnit: 'mm',
  propertiesPanelTab: 'properties',
  extrusionSettings: {
    walls: false,
    equipment: false,
    furniture: false,
    tracks: true,
  },
  showSupportPillars: false,
  showSimulationTrackNodes: false,
  showFoupPickupLabels: true,
  showOhtLabels: false,
  showOhtRouteLabels: true,
  trackNodePlacementActive: false,
  routeEndpointPlacement: null,
  routeStartPoint: null,
  routeEndPoint: null,
  trackDraw: { active: false, head: null, segmentType: 'straight' },
  pointDraw: { active: false, vertices: [], preview: null },
};

/** Drop drawing / placement tools so their overlays don't linger in Simulator. */
function deactivateEditorTools(state: UIState) {
  state.activeTool = 'pan';
  state.pointDraw = { active: false, vertices: [], preview: null };
  state.trackNodePlacementActive = false;
  state.trackDraw = { active: false, head: null, segmentType: 'straight' };
  state.placingStopForVehicle = null;
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    /**
     * Set the active tool (select, pan, draw, etc.)
     * @param state - UI state
     * @param action - Payload is tool type
     */
    setActiveTool: (state, action: PayloadAction<ToolType>) => {
      state.activeTool = action.payload;
    },

    /**
     * Set the active tab in the right drawer.
     * @param state - UI state
     * @param action - Payload is tab name
     */
    setRightDrawerTab: (state, action: PayloadAction<UIState['rightDrawerTab']>) => {
      state.rightDrawerTab = action.payload;
    },

    /**
     * Open right drawer and set active tab.
     * @param state - UI state
     * @param action - Payload is tab name
     */
    openRightDrawerTab: (state, action: PayloadAction<UIState['rightDrawerTab']>) => {
      state.rightSidebarOpen = true;
      state.rightDrawerTab = action.payload;
    },

    /**
     * Set grid visibility.
     * @param state - UI state
     * @param action - Payload is boolean
     */
    setShowGrid: (state, action: PayloadAction<boolean>) => {
      state.showGrid = action.payload;
    },

    /**
     * Set ruler visibility.
     * @param state - UI state
     * @param action - Payload is boolean
     */
    setShowRulers: (state, action: PayloadAction<boolean>) => {
      state.showRulers = action.payload;
    },

    /**
     * Set alignment guides visibility.
     * @param state - UI state
     * @param action - Payload is boolean
     */
    setShowAlignmentGuides: (state, action: PayloadAction<boolean>) => {
      state.showAlignmentGuides = action.payload;
    },

    /**
     * Toggle grid visibility state.
     */
    toggleGrid: (state) => {
      state.showGrid = !state.showGrid;
    },

    toggleDarkMode: (state) => {
      // Dark mode is disabled — light mode only.
      state.darkMode = false;
    },

    setDarkMode: (state) => {
      // Dark mode is disabled — light mode only.
      state.darkMode = false;
    },

    setLeftSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.leftSidebarOpen = action.payload;
    },

    setRightSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.rightSidebarOpen = action.payload;
    },

    setBottomPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.bottomPanelOpen = action.payload;
    },

    setDxfLayersPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.dxfLayersPanelOpen = action.payload;
    },

    toggleDxfLayersPanel: (state) => {
      state.dxfLayersPanelOpen = !state.dxfLayersPanelOpen;
    },

    setFacilityPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.facilityPanelOpen = action.payload;
    },

    toggleFacilityPanel: (state) => {
      state.facilityPanelOpen = !state.facilityPanelOpen;
    },

    setSimulationPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.simulationPanelOpen = action.payload;
      // Simulator and mindmap both use the right edge — only one at a time.
      if (action.payload) {
        state.workspaceView = 'map';
        deactivateEditorTools(state);
      }
    },

    toggleSimulationPanel: (state) => {
      state.simulationPanelOpen = !state.simulationPanelOpen;
      if (state.simulationPanelOpen) {
        state.workspaceView = 'map';
        deactivateEditorTools(state);
      }
    },

    setView3dOpen: (state, action: PayloadAction<boolean>) => {
      state.view3dOpen = action.payload;
      if (!action.payload) {
        state.view3dSceneRevealed = false;
        state.view3dSimulation = false;
        state.active3DCameraOhtId = null;
        state.active3DFollowOhtId = null;
      }
    },

    openView3d: (state, action: PayloadAction<{ simulate?: boolean } | undefined>) => {
      state.view3dOpen = true;
      state.view3dSceneRevealed = false;
      state.view3dSimulation = action.payload?.simulate === true;
    },

    closeView3d: (state) => {
      state.view3dOpen = false;
      state.view3dSceneRevealed = false;
      state.view3dSimulation = false;
      state.active3DCameraOhtId = null;
      state.active3DFollowOhtId = null;
    },

    toggleView3d: (state) => {
      state.view3dOpen = !state.view3dOpen;
      if (!state.view3dOpen) {
        state.view3dSceneRevealed = false;
        state.view3dSimulation = false;
        state.active3DCameraOhtId = null;
        state.active3DFollowOhtId = null;
      } else {
        state.view3dSceneRevealed = false;
      }
    },

    setView3dSceneRevealed: (state, action: PayloadAction<boolean>) => {
      state.view3dSceneRevealed = action.payload;
    },

    toggleView3dDragLayout: (state) => {
      state.view3dDragLayout = !state.view3dDragLayout;
    },

    setActive3DCameraPreset: (state, action: PayloadAction<CameraViewPreset>) => {
      state.active3DCameraPreset = action.payload;
      state.cameraPresetVersion = (state.cameraPresetVersion ?? 0) + 1;
      if (action.payload !== 'follow') {
        state.active3DCameraOhtId = null;
        state.active3DFollowOhtId = null;
      } else {
        state.active3DCameraOhtId = null;
      }
    },

    setActive3DCameraOht: (state, action: PayloadAction<string | null>) => {
      state.active3DCameraOhtId = action.payload;
      state.active3DFollowOhtId = null;
      state.cameraPresetVersion = (state.cameraPresetVersion ?? 0) + 1;
    },

    setActive3DFollowOht: (state, action: PayloadAction<string | null>) => {
      state.active3DFollowOhtId = action.payload;
      state.active3DCameraOhtId = null;
      state.active3DCameraPreset = 'follow';
      state.cameraPresetVersion = (state.cameraPresetVersion ?? 0) + 1;
    },
    setActive3DFollowOhtMode: (state, action: PayloadAction<UIState['active3DFollowOhtMode']>) => {
      state.active3DFollowOhtMode = action.payload;
      if (state.active3DCameraPreset === 'follow') {
        state.cameraPresetVersion = (state.cameraPresetVersion ?? 0) + 1;
      }
    },
    setToolboxSettingsOpen: (state, action: PayloadAction<boolean>) => {
      state.toolboxSettingsOpen = action.payload;
    },

    setWorkspaceView: (state, action: PayloadAction<UIState['workspaceView']>) => {
      state.workspaceView = action.payload;
      // Mindmap drawer is a 2D overlay — leave 3D and close the simulator.
      if (action.payload === 'mindmap') {
        state.view3dOpen = false;
        state.view3dSceneRevealed = false;
        state.view3dSimulation = false;
        state.simulationPanelOpen = false;
      } else {
        state.mindmapDrawerExtended = false;
      }
    },

    setMindmapDrawerExtended: (state, action: PayloadAction<boolean>) => {
      state.mindmapDrawerExtended = action.payload;
    },

    toggleMindmapDrawerExtended: (state) => {
      state.mindmapDrawerExtended = !state.mindmapDrawerExtended;
    },

    toggleLeftSidebar: (state) => {
      state.leftSidebarOpen = !state.leftSidebarOpen;
    },

    toggleRightSidebar: (state) => {
      state.rightSidebarOpen = !state.rightSidebarOpen;
    },

    toggleBottomPanel: (state) => {
      state.bottomPanelOpen = !state.bottomPanelOpen;
    },

    setZoomLevel: (state, action: PayloadAction<number>) => {
      state.zoomLevel = action.payload;
    },

    updateGridSettings: (state, action: PayloadAction<Partial<UIState['gridSettings']>>) => {
      state.gridSettings = { ...state.gridSettings, ...action.payload };
    },

    setSnapToGrid: (state, action: PayloadAction<boolean>) => {
      state.gridSettings.snapToGrid = action.payload;
    },

    setSnapToObjects: (state, action: PayloadAction<boolean>) => {
      state.gridSettings.snapToObjects = action.payload;
    },

    setGridSize: (state, action: PayloadAction<number>) => {
      state.gridSettings.size = action.payload;
    },

    setSnapGridSize: (state, action: PayloadAction<number>) => {
      state.gridSettings.snapGridSize = action.payload;
    },

    setPlacingStopForVehicle: (state, action: PayloadAction<string | null>) => {
      state.placingStopForVehicle = action.payload;
    },

    setMeasurementUnit: (state, action: PayloadAction<UIState['measurementUnit']>) => {
      state.measurementUnit = action.payload;
    },

    setPropertiesPanelTab: (state, action: PayloadAction<'properties' | '3ddepths'>) => {
      state.propertiesPanelTab = action.payload;
    },

    updateExtrusionSettings: (state, action: PayloadAction<Partial<UIState['extrusionSettings']>>) => {
      state.extrusionSettings = { ...state.extrusionSettings, ...action.payload };
    },

    setShowSupportPillars: (state, action: PayloadAction<boolean>) => {
      state.showSupportPillars = action.payload;
    },

    toggleSupportPillars: (state) => {
      state.showSupportPillars = !state.showSupportPillars;
    },

    setShowSimulationTrackNodes: (state, action: PayloadAction<boolean>) => {
      state.showSimulationTrackNodes = action.payload;
    },

    toggleSimulationTrackNodes: (state) => {
      state.showSimulationTrackNodes = !state.showSimulationTrackNodes;
    },

    setShowFoupPickupLabels: (state, action: PayloadAction<boolean>) => {
      state.showFoupPickupLabels = action.payload;
    },

    toggleFoupPickupLabels: (state) => {
      state.showFoupPickupLabels = !state.showFoupPickupLabels;
    },

    setShowOhtLabels: (state, action: PayloadAction<boolean>) => {
      state.showOhtLabels = action.payload;
    },

    toggleOhtLabels: (state) => {
      state.showOhtLabels = !state.showOhtLabels;
    },

    setShowOhtRouteLabels: (state, action: PayloadAction<boolean>) => {
      state.showOhtRouteLabels = action.payload;
    },

    toggleOhtRouteLabels: (state) => {
      state.showOhtRouteLabels = !state.showOhtRouteLabels;
    },

    setTrackNodePlacementActive: (state, action: PayloadAction<boolean>) => {
      state.trackNodePlacementActive = action.payload;
    },

    setRouteEndpointPlacement: (state, action: PayloadAction<'start' | 'end' | null>) => {
      state.routeEndpointPlacement = action.payload;
    },
    setRouteStartPoint: (state, action: PayloadAction<{ x: number; y: number } | null>) => {
      state.routeStartPoint = action.payload;
    },
    setRouteEndPoint: (state, action: PayloadAction<{ x: number; y: number } | null>) => {
      state.routeEndPoint = action.payload;
    },
    resetRouteEndpoints: (state) => {
      state.routeEndpointPlacement = null;
      state.routeStartPoint = null;
      state.routeEndPoint = null;
    },

    // ── Point-draw sketcher ──────────────────────────────────────────────────
    setPointDrawActive: (state, action: PayloadAction<boolean>) => {
      state.pointDraw.active = action.payload;
      // Always start (and leave) the mode without a pending polyline.
      state.pointDraw.vertices = [];
      state.pointDraw.preview = null;
    },

    addPointDrawVertex: (
      state,
      action: PayloadAction<{ x: number; y: number }>
    ) => {
      state.pointDraw.vertices.push(action.payload);
    },

    setPointDrawPreview: (
      state,
      action: PayloadAction<{ x: number; y: number } | null>
    ) => {
      state.pointDraw.preview = action.payload;
    },

    /** Cancel the pending polyline without leaving the mode. */
    clearPointDraw: (state) => {
      state.pointDraw.vertices = [];
      state.pointDraw.preview = null;
    },

    resetUI: (state) => {
      return { ...initialState, darkMode: state.darkMode };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(clearLayout, leave3dView)
      .addCase(loadLayout, leave3dView)
      .addCase(clearFloorPlan, leave3dView)
      .addCase(setFloorPlan, leave3dView);
  },
});

export const {
  setActiveTool,
  setRightDrawerTab,
  openRightDrawerTab,
  setShowGrid,
  setShowRulers,
  setShowAlignmentGuides,
  toggleGrid,
  toggleDarkMode,
  setDarkMode,
  setLeftSidebarOpen,
  setRightSidebarOpen,
  setBottomPanelOpen,
  setDxfLayersPanelOpen,
  toggleDxfLayersPanel,
  setFacilityPanelOpen,
  toggleFacilityPanel,
  setSimulationPanelOpen,
  toggleSimulationPanel,
  setView3dOpen,
  setView3dSceneRevealed,
  openView3d,
  closeView3d,
  toggleView3d,
  toggleView3dDragLayout,
  setActive3DCameraPreset,
  setActive3DCameraOht,
  setActive3DFollowOht,
  setActive3DFollowOhtMode,
  setToolboxSettingsOpen,
  setWorkspaceView,
  setMindmapDrawerExtended,
  toggleMindmapDrawerExtended,
  toggleLeftSidebar,
  toggleRightSidebar,
  toggleBottomPanel,
  setZoomLevel,
  updateGridSettings,
  setSnapToGrid,
  setSnapToObjects,
  setGridSize,
  setSnapGridSize,
  setPlacingStopForVehicle,
  setMeasurementUnit,
  setPropertiesPanelTab,
  updateExtrusionSettings,
  setShowSupportPillars,
  toggleSupportPillars,
  setShowSimulationTrackNodes,
  toggleSimulationTrackNodes,
  setShowFoupPickupLabels,
  toggleFoupPickupLabels,
  setShowOhtLabels,
  toggleOhtLabels,
  setShowOhtRouteLabels,
  toggleOhtRouteLabels,
  setTrackNodePlacementActive,
  setRouteEndpointPlacement,
  setRouteStartPoint,
  setRouteEndPoint,
  resetRouteEndpoints,
  setPointDrawActive,
  addPointDrawVertex,
  setPointDrawPreview,
  clearPointDraw,
  resetUI,
} = uiSlice.actions;

export default uiSlice.reducer;
